import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import TopbarControl from "../ui/buttons/TopbarControl";
import { onNewerBuild, servedBuildIsNewer } from "./appBuild";

/** How often an open tab looks for a newer build while it has the focus. */
const BUILD_CHECK_INTERVAL_MILLISECONDS = 5 * 60_000;

/**
 * "Reload to update" in the top bar, once a newer build of the app is served (F89), as web
 * apps offer "a new version is available": the tab looks when it gets the focus back and every
 * few minutes, and the API client tells it when an answer could not be read.
 */
export default function NewerBuildNotice({ className }: { className?: string }) {
  const { t } = useTranslation();
  const [newer, setNewer] = useState(false);

  useEffect(() => {
    const look = () => {
      if (document.visibilityState !== "visible") return;
      void servedBuildIsNewer().then((found) => { if (found) setNewer(true); });
    };
    const stopListening = onNewerBuild(() => { setNewer(true); });
    const interval = window.setInterval(look, BUILD_CHECK_INTERVAL_MILLISECONDS);
    window.addEventListener("focus", look);
    document.addEventListener("visibilitychange", look);
    look();
    return () => {
      stopListening();
      window.clearInterval(interval);
      window.removeEventListener("focus", look);
      document.removeEventListener("visibilitychange", look);
    };
  }, []);

  if (!newer) return null;
  return (
    <TopbarControl className={className} title={t("errors.appOutdated.title")} onClick={() => { window.location.reload(); }}>
      <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
      <span className="ml-1">{t("connection.reloadToUpdate")}</span>
    </TopbarControl>
  );
}
